import {
    chakra,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Portal,
    Stack,
    Text,
    useBoolean,
    useDisclosure,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { IoNotifications } from 'react-icons/io5';
import { useSelector } from 'react-redux';

import { attentionPulse, dashboardTokens } from '../dashboardTokens';
import NotificationItem from './NotificationItem';
import { NOTIFICATION_SEVERITY } from './notificationTypes';
import useNotifications from './useNotifications';

// Long enough for the pointer to cross from the bell to the list.
const HOVER_CLOSE_DELAY_MS = 200;

/**
 * (3153/2635) Everything that needs the company admin's attention, as a bell
 * on the account summary's header: "Attention required" with the number of
 * warnings, opening the list of them on hover or click. Renders nothing
 * until there is one.
 *
 * @param {object} props
 * @param {object} [props.serviceHealth] - reported by the service sections
 * @param {object} [props.company] - the dashboard's /Company row
 * @param {object} [props.msAuth] - the company's Microsoft Teams app auth
 * @param {boolean} [props.showLegacySyncRetirement]
 * @param {(notification: object) => void} props.onSelect
 */
const DashboardNotifications = ({
    serviceHealth,
    company,
    msAuth,
    showLegacySyncRetirement,
    onSelect,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const tokens = dashboardTokens(darkMode);
    const notifications = useNotifications({
        companyId: currentCompany,
        serviceHealth,
        company,
        msAuth,
        showLegacySyncRetirement,
    });

    // (2635) Hovering the bell or the list opens it, and leaving both closes
    // it. Closing any other way (Escape, clicking out, choosing a warning)
    // clears the hover too, so the next hover opens it again.
    const [isHovering, setIsHovering] = useBoolean(false);
    const { isOpen, onOpen, onClose } = useDisclosure({
        onClose: setIsHovering.off,
    });
    useEffect(() => {
        if (isHovering) {
            onOpen();
            return undefined;
        }
        const timer = setTimeout(onClose, HOVER_CLOSE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isHovering, onOpen, onClose]);

    if (!notifications.length) return null;

    // Errors sort first, so the first warning says how bad it gets.
    const color =
        notifications[0].severity === NOTIFICATION_SEVERITY.ERROR
            ? tokens.severity.error.accent
            : tokens.attention;
    const count = notifications.length;

    return (
        <Popover
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            placement="bottom-end"
            strategy="fixed"
            isLazy>
            <PopoverTrigger>
                <chakra.button
                    type="button"
                    onMouseEnter={setIsHovering.on}
                    onMouseLeave={setIsHovering.off}
                    // Hovering has already opened it, so a click keeps it
                    // open instead of toggling it shut under the pointer.
                    onClick={(event) => isOpen && event.preventDefault()}
                    aria-label={`Attention required: ${count} ${count === 1 ? 'warning' : 'warnings'}`}
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    px={2}
                    py={1}
                    borderRadius="md"
                    color={color}
                    _hover={{ bg: tokens.notificationHover }}
                    _focusVisible={{ boxShadow: 'outline' }}>
                    <chakra.span
                        display="inline-flex"
                        borderRadius="full"
                        sx={attentionPulse}>
                        <IoNotifications size={18} aria-hidden />
                    </chakra.span>
                    <Text as="span" fontSize="xs" fontWeight="semibold">
                        Attention required
                    </Text>
                    <chakra.span
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        minW="18px"
                        h="18px"
                        px={1}
                        borderRadius="full"
                        bg={color}
                        color="white"
                        fontSize="10px"
                        fontWeight="bold">
                        {count}
                    </chakra.span>
                </chakra.button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    onMouseEnter={setIsHovering.on}
                    onMouseLeave={setIsHovering.off}
                    rootProps={{ zIndex: 'popover' }}
                    w="380px"
                    maxW="calc(100vw - 32px)"
                    bg={darkMode ? 'gray.800' : 'white'}
                    borderColor={darkMode ? 'gray.700' : 'gray.200'}>
                    <PopoverHeader
                        fontSize="sm"
                        fontWeight="bold"
                        color={tokens.heading}
                        borderColor={darkMode ? 'gray.700' : 'gray.200'}>
                        Warnings
                    </PopoverHeader>
                    <PopoverBody p={2} maxH="60vh" overflowY="auto">
                        <Stack as="ul" aria-label="Warnings" spacing={2} m={0}>
                            {notifications.map((notification) => (
                                <NotificationItem
                                    key={notification.key}
                                    notification={notification}
                                    onSelect={(selected) => {
                                        onClose();
                                        onSelect(selected);
                                    }}
                                />
                            ))}
                        </Stack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

export default DashboardNotifications;
